/**
 * 🎓 Phase 15.1 — Tutorial Section Definitions (frontend content)
 *
 * The backend holds the 10-section catalog (titles, emojis, order,
 * critical flag); the frontend holds the STEP CONTENT for the 4
 * critical sections shipped in 15.1. The remaining 6 sections render
 * a "Coming in Phase 15.2" placeholder card in the Help Center.
 *
 * Each step is a self-contained card:
 *   {
 *     title:      "Upload Your First Track",
 *     body:       "…explanation copy…",
 *     emoji:      "🎤",
 *     cta:        { label: "Take me to Upload", to: "/submit" } | null,
 *     target:     "[data-testid=upload-track-btn]" | null  // optional spotlight
 *   }
 */
export const TUTORIAL_SECTIONS = {
  upload_track: {
    id: "upload_track",
    title: "Upload Your First Track",
    emoji: "🎤",
    steps: [
      {
        title: "Welcome to RGM Studio",
        body: "Let's get your first track live. We'll walk you through it in under a minute — you can skip anytime.",
        emoji: "👋",
      },
      {
        title: "Find the Upload button",
        body: "Tap the Upload link in the top nav (or Artist Portal on mobile). That's your launchpad for every new release.",
        emoji: "📤",
        cta: { label: "Open Upload page", to: "/submit" },
      },
      {
        title: "Title, cover art, and the file",
        body: "Drop a 1:1 square cover (1500×1500+), upload the MP3/WAV, pick a genre. The cover is what shows on the Feature Board — make it pop.",
        emoji: "🎨",
      },
      {
        title: "Hit Submit — that's it",
        body: "Your track is live the moment you submit. A&R reviews it within 24h to consider for Feature / Spotlight rotation.",
        emoji: "🚀",
      },
      {
        title: "Pro tip — share your link immediately",
        body: "Each track has a /track/<id> share URL with a branded card. Send it to your top 3 fans first — early plays + early gifts boost your momentum score.",
        emoji: "💡",
      },
    ],
  },

  edit_profile: {
    id: "edit_profile",
    title: "Edit Your Profile",
    emoji: "🪪",
    steps: [
      {
        title: "Your profile = your storefront",
        body: "Before features, before lives, before gifting — fix your profile. It's the first thing every fan sees when they tap your name.",
        emoji: "🪪",
      },
      {
        title: "Open Profile → Edit",
        body: "Click your avatar in the top right, choose Edit Profile. You can also do this from any artist link.",
        emoji: "✏️",
        cta: { label: "Open Profile Editor", to: "/profile/edit" },
      },
      {
        title: "Avatar, banner, and bio",
        body: "Square avatar 500×500+, banner 1500×500+. Bio in 1-2 sentences — what you make, where you're from. Keep it short and confident.",
        emoji: "🖼️",
      },
      {
        title: "Add your socials",
        body: "Instagram, TikTok, YouTube, Spotify — fans tap these the most after a great listen. Don't leave money on the table.",
        emoji: "🔗",
      },
      {
        title: "You're set",
        body: "Your profile is searchable, shareable, and ready to convert listeners into fans.",
        emoji: "✅",
      },
    ],
  },

  feature_spotlight: {
    id: "feature_spotlight",
    title: "Feature + Spotlight System",
    emoji: "✨",
    steps: [
      {
        title: "What is Feature Boost?",
        body: "When the A&R team features your track, it gets a 7-day promo SURGE — extra listeners, placements on the home Discovery feed, and an animated 'Feature Boost' chip on your track page.",
        emoji: "✨",
      },
      {
        title: "How the surge drips in",
        body: "Features don't dump plays all at once. The system 'drips' your campaign over 7 days: 50% in the first 24h, 80% by day 3, 100% by day 7. You'll see the progress live on your track page.",
        emoji: "💧",
      },
      {
        title: "Standard → Spotlight → Mega",
        body: "Standard features (Mon/Wed/Fri) get up to 4K play boost. Spotlight winners (top 3 weekly) unlock 10K. Mega placements (Spotlight #1 / Hero) unlock up to 30K. Each tier compounds your reach.",
        emoji: "🚀",
      },
      {
        title: "How to get featured",
        body: "There's no shortcut — but the patterns are clear. Quality cover art, original mix, consistent uploads (every 2-3 weeks), early engagement on your own track, and Beam appearances all raise A&R attention.",
        emoji: "📈",
        cta: { label: "Read the Feature Guide", to: "/how-to-get-featured" },
      },
      {
        title: "Spotlight Winners get medals",
        body: "Every Sunday the top 3 tracks of the week win Spotlight medals — 🥇 🥈 🥉 — that appear permanently on your track page as a chip. Track them in the Spotlight tab.",
        emoji: "🏆",
        cta: { label: "See current Spotlight", to: "/spotlight" },
      },
    ],
  },

  live_beam: {
    id: "live_beam",
    title: "Join or Create a Live Beam",
    emoji: "🎙️",
    steps: [
      {
        title: "What's a Beam?",
        body: "A Beam is a real-time audio/video room where you can hang with supporters, play unreleased music, take requests, and get GIFTED live.",
        emoji: "🎙️",
      },
      {
        title: "Find the Beams tab",
        body: "Tap Beams in the top nav. You'll see every live room happening right now + scheduled ones. Tap any room to join as a listener.",
        emoji: "🔴",
        cta: { label: "Open Beams", to: "/beams" },
      },
      {
        title: "Going LIVE yourself",
        body: "From Beams tap 'Start a Beam'. Give it a title (e.g. 'Unreleased Track Listening Party'), pick public or followers-only, and you're live in 5 seconds.",
        emoji: "🚀",
      },
      {
        title: "Gifts land live in your room",
        body: "When a fan sends a gift during your Beam, it flies across the screen with their name, plays a sound, and pulses your Beam Earnings panel with a +coins floater. You see the money in real time.",
        emoji: "🎁",
      },
      {
        title: "Replays + invites",
        body: "Every Beam can be recorded for replay. Use the Invite button to drop a notification to your followers — most beams 2-3x their audience after one ping.",
        emoji: "📨",
      },
    ],
  },

  // ── Phase 18.0 — Live-feature walkthroughs (was 'Coming in 15.2') ──
  coins_gifts: {
    id: "coins_gifts",
    title: "Coins & Gifts",
    emoji: "💎",
    steps: [
      {
        title: "Coins = the fuel of RGM",
        body: "Coins are the in-app currency that fans use to support artists. They buy coin packs once, then gift creators during live beams, on tracks, and on profile pages.",
        emoji: "💎",
      },
      {
        title: "Buy coins from your wallet",
        body: "Tap Wallet → Buy Coins. We support Starter ($1.99), Boost, and Whale packs — secure Stripe checkout. Coins land in your wallet instantly.",
        emoji: "🛒",
        cta: { label: "Open Wallet", to: "/wallet" },
      },
      {
        title: "Send a gift",
        body: "On any track, beam, or profile, tap the gift icon, pick an amount, and add a message. 75% of every coin goes straight to the creator's withdrawable balance.",
        emoji: "🎁",
      },
      {
        title: "Cash out (creators)",
        body: "When you receive gifts, the coin → USD ledger is automatic ($0.0075/coin, $25 minimum). Request a payout anytime from /payouts.",
        emoji: "💵",
        cta: { label: "View payout policy", to: "/payout-policy" },
      },
    ],
  },

  crews_community: {
    id: "crews_community",
    title: "Crews & Community",
    emoji: "🤝",
    steps: [
      {
        title: "Find your circle",
        body: "Crews, Groups, and Clubs let you organise around a scene, sound, or city. Members can post tracks, chat, and share opportunities privately.",
        emoji: "🤝",
      },
      {
        title: "Browse and join",
        body: "Hit the Crews tab in the nav, browse public crews, and tap Join. Some crews are invite-only — if so, you can request access or use a member's invite link.",
        emoji: "🔍",
        cta: { label: "Browse Crews", to: "/crews" },
      },
      {
        title: "Post and engage",
        body: "Inside a crew you can post tracks, drop announcements, and react with emojis. Threaded comments mean conversations don't get lost.",
        emoji: "💬",
      },
      {
        title: "Create your own crew",
        body: "Any member can create a crew. Set a name, cover, description — pick group/club/crew kind — and invite the first 5 friends. Crews grow fast when there's real activity.",
        emoji: "✨",
      },
    ],
  },

  referrals: {
    id: "referrals",
    title: "Referrals",
    emoji: "📨",
    steps: [
      {
        title: "Invite friends → earn rewards",
        body: "Every artist on RGM has a personal referral link. Friends who join via your link count toward your referral leaderboard rank and unlock badge tiers.",
        emoji: "📨",
      },
      {
        title: "Grab your link",
        body: "Visit your profile and tap Share. Your link looks like rgmstudio.org/r/<your-handle>. Drop it in your Instagram bio, Discord, or DMs.",
        emoji: "🔗",
        cta: { label: "Open my profile", to: "/profile" },
      },
      {
        title: "Track your impact",
        body: "Your Referral Leaderboard rank lives in Creator Hub. Every successful signup pushes you up the board and unlocks ranks (Bronze → Diamond).",
        emoji: "📈",
        cta: { label: "Creator Hub", to: "/creator-hub" },
      },
    ],
  },

  rankings: {
    id: "rankings",
    title: "Rankings & Charts",
    emoji: "📈",
    steps: [
      {
        title: "How charts work",
        body: "RGM charts reward momentum — plays, completion rate, gifts, and reposts in the last 7 days. Old streams don't carry the chart; new energy does.",
        emoji: "📈",
      },
      {
        title: "Hot Tracks + Weekly Top 3",
        body: "Hot Tracks rotates every few hours based on real-time momentum. Weekly Top 3 freezes Sunday midnight and locks the next week's Feature Board picks.",
        emoji: "🔥",
        cta: { label: "Open Charts", to: "/charts" },
      },
      {
        title: "Momentum chips",
        body: "When a track is gaining fast, a +momentum chip appears on its row. Sharing the chip URL to fans = direct play-to-gift conversion.",
        emoji: "⚡",
      },
    ],
  },

  share_repost: {
    id: "share_repost",
    title: "Share & Repost Music",
    emoji: "🔁",
    steps: [
      {
        title: "Every track has a share card",
        body: "Tap Share on any track and you get a branded image card with cover art, artist, and play link — pre-formatted for IG, TikTok, X, and WhatsApp.",
        emoji: "🔁",
      },
      {
        title: "Repost to your followers",
        body: "Repost surfaces a friend's track to your followers' Home feed with your reaction attached. It's the single biggest 'free promo' lever on RGM.",
        emoji: "🔂",
      },
      {
        title: "Stack co-signs",
        body: "Every repost is co-signed and visible to A&R. Tracks with 3+ artist reposts get priority in Spotlight selection.",
        emoji: "🤝",
      },
    ],
  },

  notifications: {
    id: "notifications",
    title: "Notifications",
    emoji: "🔔",
    steps: [
      {
        title: "Stay in the loop",
        body: "The bell in the top nav lights up for plays, gifts, lives, crew posts, reposts, and Spotlight pings. Tap to open the activity feed.",
        emoji: "🔔",
      },
      {
        title: "Real-time pings",
        body: "When you go live or get gifted, RGM notifies your followers instantly. Most artists 2x their first-week plays just by sending one Live ping.",
        emoji: "📡",
      },
      {
        title: "Customise what you see",
        body: "Open Settings → Notifications to fine-tune which events ping you. You can mute crews you've left or silence non-critical updates while focusing.",
        emoji: "⚙️",
        cta: { label: "Open Settings", to: "/settings" },
      },
    ],
  },
};

/** Returns the step array for a section, or [] if section is unshipped. */
export function stepsFor(sectionId) {
  return (TUTORIAL_SECTIONS[sectionId]?.steps) || [];
}

/** Returns true iff frontend has shipped step content for this section. */
export function hasShippedContent(sectionId) {
  return Array.isArray(TUTORIAL_SECTIONS[sectionId]?.steps)
    && TUTORIAL_SECTIONS[sectionId].steps.length > 0;
}
